import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatUSDT } from "@/lib/mockData";
import type { RunningOrderView } from "@/components/aiTrading/orderViewTypes";
import { useClosePosition } from "@/hooks/api/useClosePosition";
import { usePlatFormConfig } from "@/hooks/api/usePlatFormConfig";
import { ClosePositionResultCode } from "@/api/types";
import { useTranslation } from "react-i18next";
import { getTokenName, INTEGRAL_COIN_CONTRACT } from "@/config/common";
import { USDT_ADDRESS, CCC_AIF_ADDRESS } from "@/config/env";

const C = {
  card2: "rgba(22,26,34,0.72)",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  primary: "#22c55e",
};

const DIALOG_H5 = "max-w-sm w-full mx-auto sm:max-w-sm";

export default function ClosePositionDialog({
  open,
  onClose,
  order,
}: {
  open: boolean;
  onClose: () => void;
  order: RunningOrderView | null;
}) {
  const { t } = useTranslation();
  const tokenNameUsdt = getTokenName(USDT_ADDRESS);
  const tokenNameTsk = getTokenName(CCC_AIF_ADDRESS);
  const tokenNameTskx = getTokenName(INTEGRAL_COIN_CONTRACT);
  const { mutateAsync: closePositionAsync } = useClosePosition();
  const { data: platformConfig } = usePlatFormConfig();
  const [step, setStep] = useState<"confirm" | "pending" | "done">("confirm");
  useEffect(() => {
    if (open) setStep("confirm");
  }, [open, order?.id]);
  if (!order) return null;
  const handleClose = () => {
    setStep("confirm");
    onClose();
  };
  const isProfit = order.pnl >= 0;
  const toPercentText = (permille?: number) => {
    const pct = Number(permille ?? 0) / 10;
    return Number.isInteger(pct) ? `${pct}` : pct.toFixed(1);
  };
  const staticOnlyUPct = toPercentText(
    order.orderStaticOnlyURatio ?? platformConfig?.orderStaticOnlyURatio
  );
  const staticTskPct = toPercentText(
    order.orderStaticU2TskRatio ?? platformConfig?.orderStaticU2TskRatio
  );
  const staticTotalPct = toPercentText(
    order.orderStaticRatio ?? platformConfig?.orderStaticRatio
  );
  const remainingPct = toPercentText(
    1000 -
      Number(order.orderStaticRatio ?? platformConfig?.orderStaticRatio ?? 0)
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={cn(DIALOG_H5, "max-h-[85vh] overflow-y-auto")}
        style={{
          background: "#0d0f14",
          border: `1px solid ${isProfit ? "rgba(34,197,94,0.20)" : "rgba(239,68,68,0.20)"}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: isProfit ? C.green : C.red }}>
            {t("aiTradingPage.closeTitleConfirm")}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "confirm" && (
            <motion.div
              key="c"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              <div
                className="rounded-xl p-3.5 space-y-2 text-xs"
                style={{ background: C.card2, border: `1px solid ${C.border}` }}
              >
                {[
                  [t("aiTradingPage.closeFieldPair"), `${order.coin}/USDT`],
                  [
                    t("aiTradingPage.closeFieldDirection"),
                    order.type === "多"
                      ? t("aiTradingPage.badgeLong")
                      : t("aiTradingPage.badgeShort"),
                  ],
                  [
                    t("aiTradingPage.ordersMargin"),
                    `$${formatUSDT(order.margin)}`,
                  ],
                  [
                    t("aiTradingPage.detailFieldLeverage"),
                    `${order.leverage}x`,
                  ],
                  // [
                  //   "当前盈亏",
                  //   `${isProfit ? "+" : "-"}$${formatUSDT(Math.abs(order.pnlDisplay))}`,
                  // ],
                ].map(([k, v]) => (
                  <div key={k as string} className="flex justify-between">
                    <span style={{ color: C.muted }}>{k}</span>
                    <span
                      className="mono-num font-medium"
                      style={{ color: C.text }}
                    >
                      {v}
                    </span>
                  </div>
                ))}
              </div>
              {isProfit && (
                <div
                  className="rounded-xl p-2.5 text-[10px]"
                  style={{
                    background: "rgba(34,197,94,0.06)",
                    border: "1px solid rgba(34,197,94,0.12)",
                    color: C.muted,
                  }}
                >
                  {t("aiTradingPage.closeProfitAllocationHint", {
                    uPct: staticOnlyUPct,
                    tskPct: staticTskPct,
                    totalPct: staticTotalPct,
                    remainingPct,
                    tokenNameUsdt,
                    tokenNameTsk,
                  })}
                </div>
              )}
              {!isProfit && (
                <div
                  className="rounded-xl p-2.5 text-[10px]"
                  style={{
                    background: "rgba(245,158,11,0.06)",
                    border: "1px solid rgba(245,158,11,0.12)",
                    color: C.amber,
                  }}
                >
                  {t("aiTradingPage.closeLossTskxHint", { tokenNameTskx })}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleClose}
                  style={{
                    borderColor: C.border,
                    color: C.muted,
                    background: "transparent",
                  }}
                >
                  {t("aiTradingPage.dialogCancel")}
                </Button>
                <button
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm"
                  style={{
                    background: isProfit
                      ? "rgba(34,197,94,0.15)"
                      : "rgba(239,68,68,0.15)",
                    color: isProfit ? C.green : C.red,
                    border: `1px solid ${isProfit ? "rgba(34,197,94,0.30)" : "rgba(239,68,68,0.30)"}`,
                  }}
                  onClick={async () => {
                    setStep("pending");
                    try {
                      const res = await closePositionAsync({
                        // 注意：这里按后端要求传订单 id，不是 strategyId 字段值
                        strategyId: order.id,
                      });
                      if (res.resultCode === ClosePositionResultCode.Success) {
                        setStep("done");
                        return;
                      }
                      setStep("confirm");
                    } catch {
                      setStep("confirm");
                    }
                  }}
                >
                  {t("aiTradingPage.closeTitleConfirm")}
                </button>
              </div>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="p"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <RefreshCw
                  size={24}
                  className="animate-spin"
                  style={{ color: C.primary }}
                />
              </div>
              <p className="text-sm" style={{ color: C.muted }}>
                {t("aiTradingPage.closePendingMsg")}
              </p>
            </motion.div>
          )}
          {step === "done" && (
            <motion.div
              key="d"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-3"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.25)",
                }}
              >
                <CheckCircle size={28} style={{ color: C.green }} />
              </div>
              <p className="font-semibold">
                {t("aiTradingPage.closeSuccessMsg")}
              </p>
              <button
                className="w-full py-3 rounded-xl font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                {t("aiTradingPage.dialogDone")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
